export type DexcoCategory = "ICYM" | "YCS" | "YSM";

export type DexcoPostType =
  | "President"
  | "Deanery President"
  | "Vice President"
  | "Secretary"
  | "Joint Secretary/REDON Incharge"
  | "Joint Secretary"
  | "Treasurer"
  | "Animator"
  | "P.R.O/Yuvagaaz Editor"
  | "P.R.O/Yuvagaaz Co-Editor"
  | "Reporter"
  | "Cultural Secretary"
  | "Sports and Cultural Secretary"
  | "Liturgy and YouCat Incharge"
  | "Representative";

export type DexcoMember = {
  docId?: string;
  category: DexcoCategory;
  rank: number;
  name: string;
  post: string;
  postType?: DexcoPostType;
  postDetail?: string;
  deaneryName?: string;
  place?: string;
  img?: string;
};

type DexcoMemberSeed = Omit<DexcoMember, "docId" | "postType" | "postDetail" | "deaneryName">;

export const DEXCO_CATEGORY_OPTIONS: DexcoCategory[] = ["ICYM", "YCS", "YSM"];
export const DEXCO_DEANERY_OPTIONS = [
  "Kundapur Deanery",
  "Kallianpur Deanery",
  "Karkala Deanery",
  "Udupi Deanery",
  "Shirva Deanery",
];

export const DEXCO_POST_TYPE_OPTIONS: DexcoPostType[] = [
  "President",
  "Deanery President",
  "Vice President",
  "Secretary",
  "Joint Secretary/REDON Incharge",
  "Joint Secretary",
  "Treasurer",
  "Animator",
  "P.R.O/Yuvagaaz Editor",
  "P.R.O/Yuvagaaz Co-Editor",
  "Reporter",
  "Cultural Secretary",
  "Sports and Cultural Secretary",
  "Liturgy and YouCat Incharge",
  "Representative",
];

export function buildDexcoPost(
  postType: DexcoPostType,
  postDetail?: string,
  deaneryName?: string,
) {
  if (postType === "Deanery President") {
    return deaneryName?.trim() ? `President, ${deaneryName.trim()}` : "President";
  }

  if (postType === "Representative") {
    return postDetail?.trim() || "Representative";
  }

  return postType;
}

function getDefaultPostType(post?: string): DexcoPostType {
  const trimmedPost = (post || "").trim();

  if (/^President,\s+.+Deanery$/i.test(trimmedPost)) {
    return "Deanery President";
  }

  if (
    DEXCO_POST_TYPE_OPTIONS.includes(trimmedPost as DexcoPostType) &&
    trimmedPost !== "Representative"
  ) {
    return trimmedPost as DexcoPostType;
  }

  return "Representative";
}

export function normalizeDexcoMember(
  member: Omit<DexcoMember, "rank" | "post"> &
    Partial<Pick<DexcoMember, "rank" | "post" | "postType" | "postDetail" | "deaneryName">>,
) {
  const postType = member.postType || getDefaultPostType(member.post);
  const deaneryName =
    postType === "Deanery President"
      ? member.deaneryName || member.post?.replace(/^President,\s*/i, "").trim() || ""
      : "";
  const postDetail =
    postType === "Representative" ? member.postDetail || member.post || "" : "";

  return {
    ...member,
    rank:
      typeof member.rank === "number" &&
      Number.isFinite(member.rank) &&
      member.rank > 0
        ? member.rank
        : 999,
    postType,
    deaneryName,
    postDetail,
    post: buildDexcoPost(postType, postDetail, deaneryName),
  } as DexcoMember;
}

export function sortDexcoMembers(members: DexcoMember[]) {
  return [...members].sort((memberA, memberB) => {
    const categoryDifference =
      DEXCO_CATEGORY_OPTIONS.indexOf(memberA.category) -
      DEXCO_CATEGORY_OPTIONS.indexOf(memberB.category);

    if (categoryDifference !== 0) {
      return categoryDifference;
    }

    if (memberA.rank !== memberB.rank) {
      return memberA.rank - memberB.rank;
    }

    return memberA.name.localeCompare(memberB.name);
  });
}

const defaultDexcoMemberSeeds: DexcoMemberSeed[] = [
    {
      category: "ICYM",
      rank: 1,
      img: "/images/dexco/alson.png",
      name: "Alson Mathias",
      place: "Kanajar",
      post: "President",
    },
    {
      category: "ICYM",
      rank: 2,
      img: "/images/dexco/denson.png",
      name: "Denson Mascarenhas",
      place: "Kera",
      post: "Vice President",
    },
    {
      category: "ICYM",
      rank: 3,
      img: "/images/dexco/relisha.jpeg",
      name: "Relisha Fernandes",
      place: "Belman",
      post: "Secretary",
    },
    {
      category: "ICYM",
      rank: 4,
      img: "/images/dexco/adroy.jpeg",
      name: "Adroy Nazareth",
      place: "Milagres Kallianpur",
      post: "Joint Secretary/REDON Incharge",
    },
    {
      category: "ICYM",
      rank: 5,
      img: "/images/dexco/joyel.png",
      name: "Joyel Nazareth",
      place: "Byndoor",
      post: "Treasurer",
    },
    {
      category: "ICYM",
      rank: 6,
      img: "/images/dexco/lloyd.png",
      name: "Lloyd Cornelio",
      place: "Mount Rosary Santhekatte",
      post: "P.R.O/Yuvagaaz Editor",
    },
    {
      category: "ICYM",
      rank: 7,
      img: "/images/dexco/clinton.DNG",
      name: "Clinton Suares",
      place: "Udupi",
      post: "Sports and Cultural Secretary",
    },
    {
      category: "ICYM",
      rank: 8,
      img: "/images/dexco/roshni.jpeg",
      name: "Roshni Mathias",
      place: "Hirgan",
      post: "Liturgy and YouCat Incharge",
    },
    {
      category: "ICYM",
      rank: 9,
      img: "/images/dexco/godwin.jpeg",
      name: "Godwin Mascarenhas",
      place: "Mount Rosary Santhekatte",
      post: "Youth Formation Coordinator REXCO",
    },
    {
      category: "ICYM",
      rank: 10,
      img: "/images/dexco/melissa.jpeg",
      name: "Melissa Roshni Dsouza",
      place: "Kanajar",
      post: "Regional Representative",
    },
    {
      category: "ICYM",
      rank: 11,
      img: "/images/dexco/elson.png",
      name: "Elson Ashish Dsouza",
      place: "Byndoor",
      post: "President, Kundapur Deanery",
    },
    {
      category: "ICYM",
      rank: 12,
      img: "/images/dexco/roshel.png",
      name: "Roshel Dsouza",
      place: "Pethri",
      post: "President, Kallianpur Deanery",
    },
    {
      category: "ICYM",
      rank: 13,
      img: "/images/dexco/jeevan.jpeg",
      name: "Jeevan Mascarenhas",
      place: "Attur",
      post: "President, Karkala Deanery",
    },
    {
      category: "ICYM",
      rank: 14,
      img: "/images/dexco/clarilda.png",
      name: "Clarilda",
      place: "Udupi",
      post: "President, Udupi Deanery",
    },
    {
      category: "ICYM",
      rank: 15,
      img: "/images/dexco/delson.jpeg",
      name: "Delson Furtado",
      place: "Shirva",
      post: "President, Shirva Deanery",
    },
    {
      category: "YCS",
      rank: 1,
      img: "/images/ycsDexco/melwyn.jpeg",
      name: "Azil Melwyn Castelino",
      place: "Attur",
      post: "Animator",
    },
    {
      category: "YCS",
      rank: 2,
      img: "/images/ycsDexco/kavitha.jpeg",
      name: "Kavitha Cutinho",
      place: "Miyar",
      post: "Animator",
    },
    {
      category: "YCS",
      rank: 3,
      img: "/images/ycsDexco/riyan.jpeg",
      name: "Riyan Tellis",
      place: "Attur",
      post: "President",
    },
    {
      category: "YCS",
      rank: 4,
      img: "/images/ycsDexco/deon.jpeg",
      name: "Deon Dsouza",
      place: "Basrur",
      post: "Vice President",
    },
    {
      category: "YCS",
      rank: 5,
      img: "/images/ycsDexco/asheen.jpeg",
      name: "Asheen Marian Coelho",
      place: "Palimar",
      post: "Secretary",
    },
    {
      category: "YCS",
      rank: 6,
      img: "/images/ycsDexco/carol.jpeg",
      name: "Carol Quadros",
      place: "Kalmady",
      post: "Joint Secretary",
    },
    {
      category: "YCS",
      rank: 7,
      img: "/images/ycsDexco/nikhil.jpeg",
      name: "Nikhil Dsouza",
      place: "Shirva",
      post: "Treasurer",
    },
    {
      category: "YCS",
      rank: 8,
      img: "/images/ycsDexco/macklin.jpeg",
      name: "Macklin Mendonca",
      place: "Belman",
      post: "P.R.O/Yuvagaaz Co-Editor",
    },
    {
      category: "YCS",
      rank: 9,
      img: "/images/ycsDexco/rolf.jpeg",
      name: "Rolf Dsouza",
      place: "Karkal Town",
      post: "Cultural Secretary",
    },
    {
      category: "YCS",
      rank: 10,
      img: "/images/ycsDexco/vanessa.jpeg",
      name: "Vanessa Pinto",
      place: "Thottam",
      post: "Liturgy and YouCat Incharge",
    },
    {
      category: "YCS",
      rank: 11,
      img: "/images/ycsDexco/olga.jpeg",
      name: "Olga Dmello",
      place: "Piusnagar",
      post: "Student Formation Coordinator REXCO",
    },
    {
      category: "YCS",
      rank: 12,
      img: "/images/ycsDexco/melson.jpeg",
      name: "Melson Lobo",
      place: "Moodubelle",
      post: "National Representative",
    },
    {
      category: "YCS",
      rank: 13,
      img: "/images/ycsDexco/anson.jpeg",
      name: "Anson Nazareth",
      place: "Shirva",
      post: "YCS/YSM National President",
    },
    {
      category: "YCS",
      rank: 14,
      img: "/images/ycsDexco/priyanka.jpeg",
      name: "Priyanka Dalmeida",
      place: "Padukone",
      post: "President, Kundapur Deanery",
    },
    {
      category: "YCS",
      rank: 15,
      img: "/images/ycsDexco/diyona.jpeg",
      name: "Diyona Fernandes",
      place: "Thottam",
      post: "President, Kallianpur Deanery",
    },
    {
      category: "YCS",
      rank: 16,
      img: "/images/ycsDexco/jayden.jpeg",
      name: "Jayden Coelho",
      place: "Attur",
      post: "President, Karkala Deanery",
    },
    {
      category: "YCS",
      rank: 17,
      img: "/images/ycsDexco/siyana.png",
      name: "Siyana Martis",
      place: "Manipal",
      post: "President, Udupi Deanery",
    },
    {
      category: "YCS",
      rank: 18,
      img: "/images/ycsDexco/joswin.jpeg",
      name: "Joswin Aranha",
      place: "Pernal",
      post: "President, Shirva Deanery",
    },
    {
      category: "YSM",
      rank: 1,
      img: "/images/ysmDexco/prince.jpeg",
      name: "Prince Danthy",
      post: "Animator",
    },
    {
      category: "YSM",
      rank: 2,
      img: "/images/ysmDexco/alani.jpeg",
      name: "Alani Danthy",
      post: "President",
    },
    {
      category: "YSM",
      rank: 3,
      img: "/images/ysmDexco/veeksha.jpeg",
      name: "Veeksha R Acharya",
      post: "Vice President",
    },
    {
      category: "YSM",
      rank: 4,
      img: "/images/ysmDexco/irol.jpeg",
      name: "Irol Geena Dsouza",
      post: "Secretary",
    },
    {
      category: "YSM",
      rank: 5,
      img: "/images/ysmDexco/drishya.jpeg",
      name: "Dhrishya Suvarna",
      post: "Joint Secretary",
    },
    {
      category: "YSM",
      rank: 6,
      img: "/images/ysmDexco/purvi.jpeg",
      name: "Poorvi V Poojary",
      post: "Treasurer",
    },
    {
      category: "YSM",
      rank: 7,
      img: "/images/ysmDexco/nischay.jpeg",
      name: "Nischay N Kumar",
      post: "Reporter",
    },
];

export const defaultDexcoMembers: DexcoMember[] = sortDexcoMembers(
  defaultDexcoMemberSeeds.map((member) => normalizeDexcoMember(member)),
);
